/**
 * Zippopotam.us client — postal code → city + state lookup.
 *
 * Endpoint: GET https://api.zippopotam.us/{country}/{postal}
 *   200 → { "post code", "country", "country abbreviation",
 *           "places": [{"place name", "state", "state abbreviation", ...}] }
 *   404 → invalid / unknown postal code for that country
 *
 * Why direct browser fetch (no FastAPI relay):
 *   - Zippopotam serves CORS-permissive responses, so the browser can
 *     hit it without a proxy.
 *   - Means our backend doesn't have to forward the call or cache it;
 *     the user's browser does the request and the result goes nowhere
 *     else.
 *   - If we later want server-side caching / rate-limiting (e.g. to
 *     burn fewer requests during bulk admin work), we can move this
 *     behind `/api/v1/lookup/postal` and the hook below switches
 *     transparently.
 *
 * The cache is in-module Memory only — survives a session, not a
 * page reload. That's the right tradeoff for the autofill UX: we
 * don't want to re-hit the API every time the user backs into the
 * field, but stale data over a reload isn't a concern.
 */

const BASE = 'https://api.zippopotam.us';

export interface PostalLookupResult {
  /** Primary place name returned by Zippopotam (the city). */
  city: string;
  /** Full state / region name. */
  state: string;
  /** Two-letter (US) or country-equivalent abbreviation. May be empty. */
  stateAbbr: string;
}

interface ZippopotamPlace {
  'place name': string;
  state: string;
  'state abbreviation': string;
}

interface ZippopotamResponse {
  places?: ZippopotamPlace[];
}

// Keyed by `${COUNTRY}/${POSTAL}` upper-cased. A `null` value means
// "we asked and the API returned 404"; that's distinct from "we
// haven't asked yet", which `cache.has(key)` answers.
const cache = new Map<string, PostalLookupResult | null>();

function cacheKey(country: string, postal: string): string {
  return `${country.trim().toUpperCase()}/${postal.trim().toUpperCase()}`;
}

export async function lookupPostalCode(
  countryCode: string,
  postalCode: string,
  signal?: AbortSignal,
): Promise<PostalLookupResult | null> {
  const trimmedPostal = postalCode.trim();
  const trimmedCountry = countryCode.trim();
  if (!trimmedCountry || !trimmedPostal) return null;

  const key = cacheKey(trimmedCountry, trimmedPostal);
  if (cache.has(key)) {
    return cache.get(key) ?? null;
  }

  const url = `${BASE}/${trimmedCountry.toLowerCase()}/${encodeURIComponent(trimmedPostal)}`;
  const res = await fetch(url, { signal });

  if (res.status === 404) {
    cache.set(key, null);
    return null;
  }
  if (!res.ok) {
    // Don't cache transient failures — try again next time.
    throw new Error(`postal lookup failed: HTTP ${res.status}`);
  }

  const data = (await res.json()) as ZippopotamResponse;
  const place = data.places?.[0];
  if (!place) {
    cache.set(key, null);
    return null;
  }

  const result: PostalLookupResult = {
    city: place['place name'],
    state: place.state,
    stateAbbr: place['state abbreviation'] ?? '',
  };
  cache.set(key, result);
  return result;
}

/** Test helper — clears the in-memory cache. Not used in app code. */
export function _clearLookupCache(): void {
  cache.clear();
}
